import { FormControl, FormLabel, FormErrorMessage } from "@chakra-ui/react";
import { InputLeftElement, Input as ChakraInput } from "@chakra-ui/react";
import { InputGroup, InputProps as ChakraInputProps } from "@chakra-ui/react";
import { ForwardRefRenderFunction, forwardRef } from "react";
import { useState, useEffect, useCallback } from "react";
import { FieldError } from "react-hook-form";
import { IconType } from "react-icons/lib";

interface IInputProps extends ChakraInputProps {
  label?: string;
  icon?: IconType;
  name: string;
  error?: FieldError | null;
}

type inputVariationOptions = {
  [key: string]: string;
};

const inputVariation: inputVariationOptions = {
  error: "red",
  default: "gray.300",
  focus: "purple.800",
  filled: "green.500",
};

const InputBase: ForwardRefRenderFunction<HTMLInputElement, IInputProps> = (
  { label, icon: Icon, name, error = null, ...rest },
  ref
) => {
  const [variation, setVariation] = useState("default");
  const [value, setValue] = useState("");

  useEffect(() => {
    error && setVariation("error");
  }, [error]);

  const handleInputFocus = useCallback(() => {
    !error && setVariation("focus");
  }, [error]);

  const handleInputBlur = useCallback(() => {
    if (value.length > 1 && !error) {
      return setVariation("filled");
    }
  }, [error, value]);

  return (
    <FormControl isInvalid={!!error}>
      {!!label && (
        <FormLabel color="gray.400" mb="0">
          {label}
        </FormLabel>
      )}
      <InputGroup flexDirection="column">
        {Icon && (
          <InputLeftElement
            children={<Icon />}
            color={inputVariation[variation]}
            mt="1"
          />
        )}
        <ChakraInput
          name={name}
          h="35px"
          p="0px 25px"
          fontSize={["sm", "md"]}
          borderRadius="20px"
          boxShadow="md"
          bg="whitesmoke"
          color={inputVariation[variation]}
          borderColor={inputVariation[variation]}
          onFocus={handleInputFocus}
          onBlurCapture={handleInputBlur}
          onChangeCapture={(e) => setValue(e.currentTarget.value)}
          size="lg"
          variant="outline"
          _hover={{ bgColor: "gray.100" }}
          _placeholder={{ color: "gray.300" }}
          _focus={{ bg: "gray.100" }}
          ref={ref}
          {...rest}
        />
        {!!error && (
          <FormErrorMessage color="red" display="flex" justifyContent="center">
            {error.message}
          </FormErrorMessage>
        )}
      </InputGroup>
    </FormControl>
  );
};

export const Input = forwardRef(InputBase);
